/**
 * CTRL_12 Plugin - Purpose Limitation
 * Custom UI with purpose tracking, scope monitoring, and consent verification
 * Extends GenericConfigPanel with purpose compliance dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Tag,
  Space,
  List,
  Progress,
} from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  EyeOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Purpose Limitation Dashboard Component
 */
function PurposeLimitationDashboard({ config, projectId }) {
  const [compliance, setCompliance] = useState({
    withinScope: 0,
    outOfScope: 0,
    pendingReview: 0,
  });
  const [usagePurposes, setUsagePurposes] = useState([]);

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchPurposeData();
    }
  }, [config, projectId]);

  const fetchPurposeData = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setCompliance({
        withinScope: 9850,
        outOfScope: 12,
        pendingReview: 45,
      });
      setUsagePurposes([
        {
          id: 1,
          purpose: 'Customer recommendation',
          usage: 8420,
          status: 'compliant',
          consent: true,
        },
        {
          id: 2,
          purpose: 'Fraud detection',
          usage: 1430,
          status: 'compliant',
          consent: true,
        },
        {
          id: 3,
          purpose: 'Marketing analytics',
          usage: 12,
          status: 'violation',
          consent: false,
        },
      ]);
    }, 500);
  };

  const columns = [
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      key: 'purpose',
    },
    {
      title: 'Usage Count',
      dataIndex: 'usage',
      key: 'usage',
      align: 'right',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'compliant' ? 'green' : 'red';
        const icon = status === 'compliant' ? <CheckCircleOutlined /> : <WarningOutlined />;
        return (
          <Tag color={color} icon={icon}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Consent',
      dataIndex: 'consent',
      key: 'consent',
      render: (consent) => (consent ? 'Obtained' : 'Missing'),
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Purpose Limitation Disabled"
        description="Enable to track data usage against declared purposes and consent."
        type="info"
        showIcon
      />
    );
  }

  const totalUsage = compliance.withinScope + compliance.outOfScope;
  const complianceRate = totalUsage > 0 ? ((compliance.withinScope / totalUsage) * 100).toFixed(2) : 100;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Compliance Statistics */}
      <Row gutter={16}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Compliant Usage"
              value={compliance.withinScope}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Out of Scope"
              value={compliance.outOfScope}
              prefix={<WarningOutlined />}
              valueStyle={{ color: colors.status.fail }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Compliance Rate"
              value={complianceRate}
              suffix="%"
              prefix={<FileProtectOutlined />}
              valueStyle={{ color: complianceRate >= 99 ? colors.status.pass : colors.status.warning }}
            />
            <Progress
              percent={complianceRate}
              strokeColor={complianceRate >= 99 ? colors.status.pass : colors.status.warning}
              size="small"
              showInfo={false}
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Usage by Purpose */}
      <div>
        <h4>Usage by Purpose</h4>
        <Table
          columns={columns}
          dataSource={usagePurposes}
          rowKey="id"
          size="small"
          pagination={false}
        />
      </div>

      {/* Declared Purposes */}
      <Card size="small" title="Declared Purposes">
        <List
          size="small"
          dataSource={config?.declared_purposes || []}
          renderItem={(purpose) => (
            <List.Item>
              <Space>
                <EyeOutlined style={{ color: colors.primary[500] }} />
                {purpose}
              </Space>
            </List.Item>
          )}
        />
      </Card>

      {compliance.outOfScope > 0 && (
        <Alert
          message={`${compliance.outOfScope} out-of-scope usage detected`}
          description="Data is being used for purposes outside declared scope."
          type="error"
          showIcon
        />
      )}
    </Space>
  );
}

/**
 * CTRL_12 Plugin Component
 */
function CTRL_12_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    declared_purposes: ['recommendation', 'fraud_detection'],
  });

  return (
    <div>
      {/* Purpose Dashboard */}
      <PurposeLimitationDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_12_Plugin;
